/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { Fragment } from 'react'

import { EuiHealth, EuiLink, EuiSpacer, EuiTableRowCell, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { CuiTableColumn } from '@modules/cui/Table'
import { CuiHelpTipIcon } from '@modules/cui/HelpTipIcon'
import CuiElasticConsumptionUnits from '@modules/cui/formatters/CuiElasticConsumptionUnits'
import type { DeploymentInstance } from '@modules/billing-api/customTypes'
import { colorForInstances } from '@modules/billing-lib/colorGenerator'

import BillingUsageTable from '@/components/Billing/BillingUsage/BillingUsageOverviewV2/BillingUsageTable'

import AggregationTableRowV2 from '../components/AggregationTableRowV2'
import { ColorKeyForOtherDeployments, VisibleInstances } from '../constants'

import {
  getDeploymentInstancesWithAggregatedCosts,
  getRowColor,
  getTotalDeploymentCostsPerDimension,
} from './utils'

import type {
  DeploymentInstanceWithAggregatedCosts,
  TotalDeploymentCostsPerDimension,
} from './types'
import type { FunctionComponent } from 'react'

interface Props {
  instances: DeploymentInstance[]
  isAggregated: boolean
  isLoading: boolean
  onSelect: (id: string, name?: string) => void
  organizationId: string
}

const DeploymentsList: FunctionComponent<Props> = ({
  instances,
  isLoading,
  onSelect,
  isAggregated,
  organizationId,
}) => {
  const items = getDeploymentInstancesWithAggregatedCosts(instances).sort(
    (a, b) => b.total_ecu - a.total_ecu,
  )
  const totalDeploymentCostsPerDimension = getTotalDeploymentCostsPerDimension(items)
  const visibleItems = items.slice(0, VisibleInstances)
  const collapsedItems = items.slice(VisibleInstances)
  const collapsedTotals = getTotalDeploymentCostsPerDimension(collapsedItems)

  const columns: Array<CuiTableColumn<DeploymentInstanceWithAggregatedCosts>> = [
    {
      label: (
        <FormattedMessage id='billing-usage-deployment-list.name-label' defaultMessage='Name' />
      ),
      width: '30%',
      render: ({ name, id }) => (
        <EuiHealth
          color={
            isAggregated ? colorForInstances.get(ColorKeyForOtherDeployments) : getRowColor(id)
          }
        >
          <EuiLink disabled={!id} onClick={() => onSelect(id, name)}>
            <span data-test-id='name'>{name || id}</span>
          </EuiLink>
          {name && (
            <Fragment>
              <EuiSpacer size='xs' />
              <EuiText color='subdued' size='xs'>
                {id}
              </EuiText>
            </Fragment>
          )}
        </EuiHealth>
      ),
      footer: {
        render: () => (
          <Fragment>
            <FormattedMessage
              id='billing-usage-deployment-list.total'
              defaultMessage='Total usage'
            />
            <CuiHelpTipIcon iconType='info'>
              <FormattedMessage
                id='billing-deployment-usage.tip-icon'
                defaultMessage='Usage only. Does not include credits, prepaids, or any other discounts.'
              />
            </CuiHelpTipIcon>
          </Fragment>
        ),
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage
          id='billing-usage-deployment-list.capacity-label'
          defaultMessage='Capacity (in ECU)'
        />
      ),
      render: ({ capacity }) => (
        <span data-test-id='capacity'>
          <CuiElasticConsumptionUnits value={capacity} unit='none' dp={4} withSymbol={false} />
        </span>
      ),
      footer: {
        render: () => (
          <span data-test-id='total-capacity'>
            <CuiElasticConsumptionUnits
              value={totalDeploymentCostsPerDimension.capacity}
              unit='none'
              dp={4}
              withSymbol={false}
              symbolPosition='before'
            />
          </span>
        ),
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage
          id='billing-usage-deployment-list.data-transfer-label'
          defaultMessage='Data transfer (in ECU)'
        />
      ),
      render: ({ dataTransfer }) => (
        <span data-test-id='dataTransfer'>
          <CuiElasticConsumptionUnits value={dataTransfer} unit='none' dp={4} withSymbol={false} />
        </span>
      ),
      footer: {
        render: () => (
          <span data-test-id='total-dataTransfer'>
            <CuiElasticConsumptionUnits
              value={totalDeploymentCostsPerDimension.dataTransfer}
              unit='none'
              dp={4}
              withSymbol={false}
              symbolPosition='before'
            />
          </span>
        ),
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage
          id='billing-usage-deployment-list.snapshots-label'
          defaultMessage='Snapshots (in ECU)'
        />
      ),
      render: ({ storage }) => (
        <span data-test-id='storage'>
          <CuiElasticConsumptionUnits value={storage} unit='none' dp={4} withSymbol={false} />
        </span>
      ),
      footer: {
        render: () => (
          <span data-test-id='total-storage'>
            <CuiElasticConsumptionUnits
              value={totalDeploymentCostsPerDimension.storage}
              unit='none'
              dp={4}
              withSymbol={false}
              symbolPosition='before'
            />
          </span>
        ),
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage
          id='billing-usage-deployment-list.synthetics-label'
          defaultMessage='Synthetics (in ECU)'
        />
      ),
      render: ({ synthetics }) => (
        <span data-test-id='synthetics'>
          <CuiElasticConsumptionUnits value={synthetics} unit='none' dp={4} withSymbol={false} />
        </span>
      ),
      footer: {
        render: () => (
          <span data-test-id='total-synthetics'>
            <CuiElasticConsumptionUnits
              value={totalDeploymentCostsPerDimension.synthetics}
              unit='none'
              dp={4}
              withSymbol={false}
              symbolPosition='before'
            />
          </span>
        ),
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage
          id='billing-usage-deployment-list.inference-label'
          defaultMessage='Inference (in ECU)'
        />
      ),
      render: ({ inference }) => (
        <span data-test-id='inference'>
          <CuiElasticConsumptionUnits value={inference} unit='none' dp={4} withSymbol={false} />
        </span>
      ),
      footer: {
        render: () => (
          <span data-test-id='total-inference'>
            <CuiElasticConsumptionUnits
              value={totalDeploymentCostsPerDimension.inference}
              unit='none'
              dp={4}
              withSymbol={false}
              symbolPosition='before'
            />
          </span>
        ),
      },
    },
    {
      align: 'right',
      label: (
        <FormattedMessage
          id='billing-usage-deployment-list.total-label'
          defaultMessage='Total (in ECU)'
        />
      ),
      render: ({ total_ecu }) => (
        <span data-test-id='total'>
          <CuiElasticConsumptionUnits value={total_ecu} unit='none' dp={4} withSymbol={false} />
        </span>
      ),
      footer: {
        render: () => (
          <span data-test-id='total-total'>
            <CuiElasticConsumptionUnits
              value={totalDeploymentCostsPerDimension.total_ecu}
              unit='none'
              dp={4}
              withSymbol={false}
              symbolPosition='before'
            />
          </span>
        ),
      },
    },
    {
      actions: true, // additional actions column
      width: '40px',
    },
  ]

  return (
    <BillingUsageTable<DeploymentInstanceWithAggregatedCosts>
      title={
        <FormattedMessage
          id='billing-deployment-usage-list.title'
          defaultMessage='Hosted deployments'
        />
      }
      rows={visibleItems}
      columns={columns}
      isLoading={isLoading}
      organizationId={organizationId}
      dataTestId='deployments-list-usage-table'
      renderCustomRowsLast={() => (
        <AggregationTableRowV2<
          TotalDeploymentCostsPerDimension,
          DeploymentInstanceWithAggregatedCosts
        >
          onItemSelected={onSelect}
          totals={collapsedTotals}
          items={collapsedItems}
          type='deployment'
          renderRow={({ capacity, dataTransfer, storage, synthetics, inference, total_ecu }) => (
            <Fragment>
              <EuiTableRowCell align='right'>
                <CuiElasticConsumptionUnits
                  value={capacity}
                  unit='none'
                  dp={4}
                  withSymbol={false}
                />
              </EuiTableRowCell>
              <EuiTableRowCell align='right'>
                <span data-test-id='deployments-list-dataTransfer'>
                  <CuiElasticConsumptionUnits
                    value={dataTransfer}
                    unit='none'
                    dp={4}
                    withSymbol={false}
                  />
                </span>
              </EuiTableRowCell>
              <EuiTableRowCell align='right'>
                <span data-test-id='deployments-list-storage'>
                  <CuiElasticConsumptionUnits
                    value={storage}
                    unit='none'
                    dp={4}
                    withSymbol={false}
                  />
                </span>
              </EuiTableRowCell>
              <EuiTableRowCell align='right'>
                <span data-test-id='deployments-list-synthetics'>
                  <CuiElasticConsumptionUnits
                    value={synthetics}
                    unit='none'
                    dp={4}
                    withSymbol={false}
                  />
                </span>
              </EuiTableRowCell>
              <EuiTableRowCell align='right'>
                <span data-test-id='deployments-list-inference'>
                  <CuiElasticConsumptionUnits
                    value={inference}
                    unit='none'
                    dp={4}
                    withSymbol={false}
                  />
                </span>
              </EuiTableRowCell>
              <EuiTableRowCell align='right'>
                <span data-test-id='deployments-list-total'>
                  <CuiElasticConsumptionUnits
                    value={total_ecu}
                    unit='none'
                    dp={4}
                    withSymbol={false}
                  />
                </span>
              </EuiTableRowCell>
              <EuiTableRowCell className='euiTableCellContent' />
            </Fragment>
          )}
        />
      )}
    />
  )
}

export default DeploymentsList
